/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BUTTON_FORMAT_TRANSPARENT, BUTTON_THEME_DEFAULT, Button, ICON_ADD, TextField } from '@squiz/sds';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EVENT_SUBSCRIPTION_BUILDER_DATA_IDS,
  EVENT_SUBSCRIPTION_BUILDER_FORM_IDS,
} from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.constants';
import { AttributeRule } from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.defs';
import { EventSubscriptionAttributeFilter } from '../EventSubscriptionAttributeFilter/EventSubscriptionAttributeFilter';
import { EventSubscriptionAttributesProps } from './EventSubscriptionAttributes.props';

/**
 * The event subscriptions attributes section.
 */
export const EventSubscriptionAttributes = (props: EventSubscriptionAttributesProps): JSX.Element => {
  const { t } = useTranslation();
  const { attributeFilters, eventType, fieldErrors, setAttributeFilters, setFieldErrors } = props;

  // Labels
  const labels = useMemo(() => {
    return {
      addAttribute: t(CONFIGURATION_STRINGS.addAttribute),
      and: t(CONFIGURATION_STRINGS.and),
      attributeFiltersDescription: t(CONFIGURATION_STRINGS.attributeFiltersDescription),
      attributeFiltersOptional: t(CONFIGURATION_STRINGS.attributeFiltersOptional),
      ruleMatchingLogic: t(CONFIGURATION_STRINGS.ruleMatchingLogic),
    };
  }, [t]);

  const addAttributeFilter = useCallback((): void => {
    const newAttributeFilter = {
      id: window.crypto.randomUUID(),
      name: '',
      rule: '' as AttributeRule,
      value: '',
    };

    setAttributeFilters([...attributeFilters, newAttributeFilter]);
  }, [attributeFilters, setAttributeFilters]);

  const addAttributeFilterButton = useMemo(() => {
    return (
      <Button
        data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.addAttribute}
        format={BUTTON_FORMAT_TRANSPARENT}
        icon={ICON_ADD}
        label={labels.addAttribute}
        onClick={addAttributeFilter}
        text={labels.addAttribute}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
  }, [addAttributeFilter, labels]);

  const getAttributeFilters = useCallback((): JSX.Element => {
    return (
      <>
        <div className="event-subscription-attribute-filters__rows">
          {attributeFilters.map((filter) => {
            return (
              <EventSubscriptionAttributeFilter
                attributeFilters={attributeFilters}
                eventType={eventType}
                fieldErrors={fieldErrors}
                id={filter.id}
                key={filter.id}
                setAttributeFilters={setAttributeFilters}
                setFieldErrors={setFieldErrors}
              />
            );
          })}
        </div>
        <div className="event-subscription-attribute-filters__footer">
          {addAttributeFilterButton}
          <div className="event-subscription-attribute-filters__rule-matching-logic">
            <span className="sds-form-label">{labels.ruleMatchingLogic}</span>
            <TextField
              disabled
              fieldLabel={labels.and}
              hideLabel
              id={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.ruleMatchingLogic}
              name={EVENT_SUBSCRIPTION_BUILDER_FORM_IDS.ruleMatchingLogic}
              value={labels.and}
            />
          </div>
        </div>
      </>
    );
  }, [addAttributeFilterButton, attributeFilters, eventType, fieldErrors, labels, setAttributeFilters, setFieldErrors]);

  const attributeFiltersContainerClass = clsx(`event-subscription-attribute-filters__container`, {
    [`event-subscription-attribute-filters__container--empty`]: !attributeFilters.length,
  });

  return (
    <div className="event-subscription__attribute-filters">
      <h3>{labels.attributeFiltersOptional}</h3>
      <p>{labels.attributeFiltersDescription}</p>
      <div className={attributeFiltersContainerClass}>
        {attributeFilters.length ? getAttributeFilters() : addAttributeFilterButton}
      </div>
    </div>
  );
};
